<script setup lang="ts">
import { isJust, just, nothing, type Maybe } from "../lib/maybe";
import { computed, onUnmounted, ref, watch } from "vue";
import ZwButton from "./ui/ZwButton.vue";
import ZwDialog from "./ui/ZwDialog.vue";

const props = defineProps<{
  open: boolean;
  dataUrl: string;
  title?: string;
}>();

const emit = defineEmits<{
  close: [];
  apply: [payload: { x: number; y: number; width: number; height: number }];
}>();

type CropRect = { x: number; y: number; width: number; height: number };
type Handle = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

const HANDLES: Handle[] = ["nw", "n", "ne", "w", "e", "sw", "s", "se"];

const natural = ref({ width: 0, height: 0 });
const crop = ref<CropRect>({ x: 0, y: 0, width: 0, height: 0 });
const dragging = ref(false);
const dragMode = ref<"move" | "resize">("move");
const activeHandle = ref<Maybe<Handle>>(nothing);
const dragOrigin = ref({
  x: 0,
  y: 0,
  crop: { x: 0, y: 0, width: 0, height: 0 } as CropRect,
});

const displayScale = computed(() => {
  if (natural.value.width === 0) return 1;
  const maxW = Math.min(720, window.innerWidth - 80);
  const maxH = Math.min(520, window.innerHeight - 180);
  return Math.min(1, maxW / natural.value.width, maxH / natural.value.height);
});

const frameStyle = computed(() => ({
  width: `${natural.value.width * displayScale.value}px`,
  height: `${natural.value.height * displayScale.value}px`,
}));

const overlayStyle = computed(() => ({
  left: `${crop.value.x * displayScale.value}px`,
  top: `${crop.value.y * displayScale.value}px`,
  width: `${crop.value.width * displayScale.value}px`,
  height: `${crop.value.height * displayScale.value}px`,
}));

function resetCrop(width: number, height: number): void {
  crop.value = { x: 0, y: 0, width, height };
}

function onImageLoad(event: Event): void {
  const img = event.target as HTMLImageElement;
  natural.value = { width: img.naturalWidth, height: img.naturalHeight };
  resetCrop(img.naturalWidth, img.naturalHeight);
}

function clampCrop(): void {
  const maxW = natural.value.width;
  const maxH = natural.value.height;
  let { x, y, width, height } = crop.value;
  width = Math.max(24, Math.min(width, maxW));
  height = Math.max(24, Math.min(height, maxH));
  x = Math.max(0, Math.min(x, maxW - width));
  y = Math.max(0, Math.min(y, maxH - height));
  crop.value = { x, y, width, height };
}

function resizeCrop(
  orig: CropRect,
  handle: Handle,
  dx: number,
  dy: number,
): CropRect {
  let { x, y, width, height } = orig;
  const min = 24;

  if (handle.includes("e")) {
    width = Math.max(min, orig.width + dx);
  }
  if (handle.includes("w")) {
    width = Math.max(min, orig.width - dx);
    x = orig.x + orig.width - width;
  }
  if (handle.includes("s")) {
    height = Math.max(min, orig.height + dy);
  }
  if (handle.includes("n")) {
    height = Math.max(min, orig.height - dy);
    y = orig.y + orig.height - height;
  }

  return { x, y, width, height };
}

function startDrag(
  mode: "move" | "resize",
  handle: Maybe<Handle>,
  event: PointerEvent,
): void {
  dragging.value = true;
  dragMode.value = mode;
  activeHandle.value = handle;
  dragOrigin.value = {
    x: event.clientX,
    y: event.clientY,
    crop: { ...crop.value },
  };
  window.addEventListener("pointermove", onWindowPointerMove);
  window.addEventListener("pointerup", onWindowPointerUp);
  window.addEventListener("pointercancel", onWindowPointerUp);
}

function stopDrag(): void {
  dragging.value = false;
  activeHandle.value = nothing;
  window.removeEventListener("pointermove", onWindowPointerMove);
  window.removeEventListener("pointerup", onWindowPointerUp);
  window.removeEventListener("pointercancel", onWindowPointerUp);
}

function onWindowPointerMove(event: PointerEvent): void {
  if (dragging.value === false) return;
  const scale = displayScale.value > 0 ? displayScale.value : 1;
  const dx = (event.clientX - dragOrigin.value.x) / scale;
  const dy = (event.clientY - dragOrigin.value.y) / scale;
  const orig = dragOrigin.value.crop;

  if (dragMode.value === "move") {
    crop.value = {
      x: orig.x + dx,
      y: orig.y + dy,
      width: orig.width,
      height: orig.height,
    };
  } else if (isJust(activeHandle.value)) {
    crop.value = resizeCrop(orig, activeHandle.value.value, dx, dy);
  }

  clampCrop();
}

function onWindowPointerUp(): void {
  stopDrag();
}

function onMovePointerDown(event: PointerEvent): void {
  event.preventDefault();
  startDrag("move", nothing, event);
}

function onHandlePointerDown(handle: Handle, event: PointerEvent): void {
  event.preventDefault();
  event.stopPropagation();
  startDrag("resize", just(handle), event);
}

function apply(): void {
  if (natural.value.width === 0 || natural.value.height === 0) return;
  clampCrop();
  emit("apply", { ...crop.value });
}

watch(
  () => props.open,
  (open) => {
    if (open === false) {
      stopDrag();
      natural.value = { width: 0, height: 0 };
    }
  },
);

onUnmounted(() => {
  stopDrag();
});
</script>

<template>
  <ZwDialog
    :open="open"
    :title="title ?? 'Crop image'"
    :description="just('Drag to move. Pull the handles to resize.')"
    size="lg"
    layout="sheet"
    :z-index="110"
    @close="emit('close')"
  >
    <div class="image-crop-body">
      <div class="image-crop-frame" :style="frameStyle">
        <img
          :key="dataUrl"
          :src="dataUrl"
          alt=""
          class="image-crop-source"
          draggable="false"
          @load="onImageLoad"
        />
        <div
          v-if="natural.width > 0"
          class="image-crop-overlay"
          :style="overlayStyle"
          @pointerdown="onMovePointerDown"
        >
          <div
            v-for="handle in HANDLES"
            :key="handle"
            class="image-crop-handle"
            :class="`image-crop-handle-${handle}`"
            @pointerdown="onHandlePointerDown(handle, $event)"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <ZwButton @click="emit('close')">Cancel</ZwButton>
      <ZwButton
        variant="primary"
        :disabled="natural.width === 0"
        @click="apply"
      >
        Apply crop
      </ZwButton>
    </template>
  </ZwDialog>
</template>

<style>
.image-crop-body {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 1rem 1.1rem;
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.image-crop-frame {
  position: relative;
  overflow: hidden;
  border: 1px solid color-mix(in oklch, var(--line) 90%, transparent);
  border-radius: 0.35rem;
  background: color-mix(in oklch, var(--bg) 75%, var(--bg-raised));
}

.image-crop-source {
  display: block;
  width: 100%;
  height: 100%;
  user-select: none;
  pointer-events: none;
}

.image-crop-overlay {
  position: absolute;
  border: 2px solid var(--color-zw-amber);
  box-shadow: 0 0 0 9999px
    color-mix(in oklch, var(--text-strong) 42%, transparent);
  cursor: move;
  touch-action: none;
}

.image-crop-handle {
  position: absolute;
  width: 0.65rem;
  height: 0.65rem;
  border: 2px solid var(--color-zw-amber);
  border-radius: 999px;
  background: var(--bg-raised);
  touch-action: none;
}

.image-crop-handle-n,
.image-crop-handle-s {
  left: 50%;
  transform: translateX(-50%);
}

.image-crop-handle-e,
.image-crop-handle-w {
  top: 50%;
  transform: translateY(-50%);
}

.image-crop-handle-n {
  top: -0.35rem;
  cursor: n-resize;
}

.image-crop-handle-s {
  bottom: -0.35rem;
  cursor: s-resize;
}

.image-crop-handle-e {
  right: -0.35rem;
  cursor: e-resize;
}

.image-crop-handle-w {
  left: -0.35rem;
  cursor: w-resize;
}

.image-crop-handle-ne {
  top: -0.35rem;
  right: -0.35rem;
  cursor: ne-resize;
}

.image-crop-handle-nw {
  top: -0.35rem;
  left: -0.35rem;
  cursor: nw-resize;
}

.image-crop-handle-se {
  right: -0.35rem;
  bottom: -0.35rem;
  cursor: se-resize;
}

.image-crop-handle-sw {
  left: -0.35rem;
  bottom: -0.35rem;
  cursor: sw-resize;
}
</style>
